/**
 * goose-controller.js — the dual-mode player avatar (GDD 3).
 *
 * Third person in the hub: you steer a goose and the camera follows over its
 * shoulder on a smooth rail. First person at a stall: the goose body is hidden,
 * the camera sits at eye height at the stall's play origin, and wing-hands
 * appear on the controllers.
 *
 * The camera rig is the player's world transform. In XR the headset pose is
 * applied to the camera *relative to* the rig, so this controller only ever
 * moves the rig — never the camera's world position directly. That is what
 * makes one implementation correct in both VR and on the desktop.
 *
 * @module avatar/goose-controller
 */

import * as THREE from 'three';
import { AVATAR, HUB, EVENTS } from '../constants.js';
import { bus } from '../core/events.js';
import { input } from '../core/input.js';
import { teleport } from '../core/teleport.js';
import { createGoose } from '../assets3d/factory.js';
import { wingHands } from './wing-hands.js';
import { cosmetics } from './cosmetics.js';

/** @typedef {'third'|'first'} AvatarMode */

class GooseController {
  constructor() {
    /** @type {THREE.Group} */ this.goose = null;
    /** @type {THREE.Object3D} */ this.rig = null;
    /** @type {THREE.Camera} */ this.camera = null;
    /** @type {THREE.Scene} */ this.scene = null;

    /** @type {AvatarMode} */ this.mode = 'third';
    this.enabled = true;

    /** Goose world position (floor level). */
    this.position = new THREE.Vector3();
    /** Goose facing, radians. */
    this.facing = Math.PI;

    /** Desktop camera orbit. */
    this.yaw = Math.PI;
    this.pitch = -0.16;

    /** First-person anchor, set on stall entry. */
    this._firstPersonAnchor = { position: new THREE.Vector3(), yaw: 0 };

    this._bobPhase = 0;
    this._idleTime = 0;
    this._speed = 0;
    this._transitioning = false;

    /** @type {Array<(dt:number, ctx:object)=>void>} */
    this._waddleHooks = [];
    /** @type {Array<(dt:number, ctx:object)=>void>} */
    this._idleHooks = [];

    this._scratch = { v: new THREE.Vector3(), v2: new THREE.Vector3(), q: new THREE.Quaternion() };
  }

  /**
   * @param {THREE.Scene} scene
   * @param {THREE.Object3D} rig The engine's camera rig.
   * @param {THREE.Camera} camera
   */
  init(scene, rig, camera) {
    this.scene = scene;
    this.rig = rig;
    this.camera = camera;

    this.goose = createGoose();
    this.goose.name = 'player-goose';
    scene.add(this.goose);

    cosmetics.init(this.goose);
    wingHands.init(camera);

    // The avatar owns the rig, so it must perform teleport moves itself.
    teleport.setMover((position, yaw) => this.setPosition(position, yaw));

    this.setPosition(HUB.spawn, HUB.spawnYaw);
    this.setMode('third', { instant: true });
    return this;
  }

  // ── Placement ─────────────────────────────────────────────────────────────

  /**
   * Teleport the goose (and the camera) somewhere. No blink — callers that
   * want comfort handling should go through `teleport`.
   * @param {THREE.Vector3|{x,y,z}} position
   * @param {number} [yaw]
   */
  setPosition(position, yaw = null) {
    this.position.set(position.x, position.y || 0, position.z);
    if (yaw !== null && yaw !== undefined) {
      this.facing = yaw;
      this.yaw = yaw;
    }
    // Keep the stall anchor in step, or a teleport taken in first person would
    // move the goose while the camera stayed at the old booth.
    if (this.mode === 'first') {
      this._firstPersonAnchor.position.copy(this.position);
      this._firstPersonAnchor.yaw = this.facing;
    }
    this.goose.position.copy(this.position);
    this.goose.rotation.y = this.facing + Math.PI;
    this._applyCamera(1);
  }

  /** @returns {THREE.Vector3} A copy of the goose's floor position. */
  getPosition() {
    return this.position.clone();
  }

  /** @returns {AvatarMode} */
  getMode() {
    return this.mode;
  }

  // ── Mode switching ────────────────────────────────────────────────────────

  /**
   * Switch between hub goose and stall wing-hands (GDD 3.3).
   * @param {AvatarMode} mode
   * @param {{ position?: THREE.Vector3|{x,y,z}, yaw?: number, instant?: boolean }} [opts]
   * @returns {Promise<void>} Resolves once the transition fade completes.
   */
  setMode(mode, opts = {}) {
    if (this.mode === mode && !opts.position) return Promise.resolve();

    const apply = () => {
      this.mode = mode;
      if (mode === 'first') {
        const p = opts.position || this.position;
        this._firstPersonAnchor.position.set(p.x, p.y || 0, p.z);
        this._firstPersonAnchor.yaw = opts.yaw ?? this.facing;
        // The player really is standing at the stall now — keep the avatar
        // position in step so `getPosition()` stays truthful in both modes and
        // the goose reappears here on exit.
        this.position.copy(this._firstPersonAnchor.position);
        this.facing = this._firstPersonAnchor.yaw;
        this.goose.position.copy(this.position);
        this.yaw = this._firstPersonAnchor.yaw;
        // A slight downward tilt so the counter and the objects on it are in
        // frame on arrival, not below it.
        this.pitch = -0.12;
        this.goose.visible = false;
        wingHands.setVisible(true);
        teleport.setMarkersVisible(false);
      } else {
        this.goose.visible = true;
        wingHands.setVisible(false);
        teleport.setMarkersVisible(true);
        if (opts.position) {
          this.position.set(opts.position.x, opts.position.y || 0, opts.position.z);
        }
        if (opts.yaw !== undefined) { this.facing = opts.yaw; this.yaw = opts.yaw; }
        this.pitch = -0.16;
      }
      this._applyCamera(1);
      bus.emit(EVENTS.AVATAR_MODE, { mode, position: this.position.clone() });
    };

    if (opts.instant) {
      apply();
      return Promise.resolve();
    }

    this._transitioning = true;
    return new Promise((resolve) => {
      teleport.blink(() => apply(), () => {
        this._transitioning = false;
        resolve();
      });
    });
  }

  // ── Animation hooks ───────────────────────────────────────────────────────

  /**
   * Run a callback each frame while the goose is walking.
   * The polish agent hangs the real waddle animation here.
   * @param {(dt: number, ctx: {speed: number, phase: number, goose: THREE.Group}) => void} fn
   * @returns {() => void} Unregister.
   */
  onWaddle(fn) {
    this._waddleHooks.push(fn);
    return () => { this._waddleHooks = this._waddleHooks.filter((f) => f !== fn); };
  }

  /**
   * Run a callback each frame while the goose is standing still.
   * @param {(dt: number, ctx: {idleTime: number, goose: THREE.Group}) => void} fn
   * @returns {() => void} Unregister.
   */
  onIdle(fn) {
    this._idleHooks.push(fn);
    return () => { this._idleHooks = this._idleHooks.filter((f) => f !== fn); };
  }

  // ── Frame update ──────────────────────────────────────────────────────────

  update(dt) {
    if (!this.enabled) return;
    wingHands.update(dt);

    if (this.mode === 'first') {
      this._updateFirstPerson(dt);
      return;
    }
    this._updateThirdPerson(dt);
  }

  /** @private */
  _updateThirdPerson(dt) {
    // Mouse look orbits the camera; the goose walks relative to that view.
    if (!input.isVR) {
      const look = input.consumeLook();
      this.yaw -= look.x;
      this.pitch = THREE.MathUtils.clamp(this.pitch - look.y, -0.9, 0.5);
    } else if (input.turnAxis) {
      this.yaw -= input.turnAxis * AVATAR.turnSpeed * dt;
    }

    // VR movement is teleport-only (GDD 12.3) — the stick does not walk.
    const canWalk = !input.isVR;
    let moved = false;
    if (canWalk && !this._transitioning) {
      const ax = input.moveAxis.x;
      const ay = input.moveAxis.y;
      if (ax || ay) {
        // Facing convention: a yaw of θ means "heading (sin θ, 0, cos θ)".
        const forward = this._scratch.v.set(Math.sin(this.yaw), 0, Math.cos(this.yaw));
        const right = this._scratch.v2.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw));
        const move = forward.multiplyScalar(ay).add(right.multiplyScalar(ax));
        if (move.lengthSq() > 0) {
          move.normalize().multiplyScalar(AVATAR.moveSpeed * dt);
          this.position.add(move);
          this._clampToHub();
          // Face the direction of travel.
          const targetFacing = Math.atan2(move.x, move.z);
          this.facing = angleLerp(this.facing, targetFacing, Math.min(1, dt * 9));
          moved = true;
        }
      }
    }

    this._speed = moved ? AVATAR.moveSpeed : 0;
    this._animate(dt, moved);

    this.goose.position.copy(this.position);
    this.goose.rotation.y = this.facing + Math.PI;
    this._applyCamera(Math.min(1, dt * AVATAR.followLerp));
  }

  /** @private */
  _updateFirstPerson(dt) {
    if (!input.isVR) {
      const look = input.consumeLook();
      this.yaw -= look.x;
      this.pitch = THREE.MathUtils.clamp(this.pitch - look.y, -1.2, 1.2);
    }
    this._applyCamera(1);
  }

  /**
   * Waddle bob and idle breathing. Real animation clips will replace the
   * procedural motion, but the hooks stay the same.
   * @private
   */
  _animate(dt, moving) {
    const parts = this.goose.userData.parts;
    if (moving) {
      this._idleTime = 0;
      this._bobPhase += dt * AVATAR.bobFrequency;
      const bob = Math.sin(this._bobPhase) * AVATAR.bobAmplitude;
      const sway = Math.cos(this._bobPhase * 0.5) * 0.055;
      this.goose.position.y = this.position.y + Math.abs(bob);
      this.goose.rotation.z = sway;
      if (parts) parts.neck.rotation.x = -0.22 + Math.sin(this._bobPhase) * 0.07;
      for (const fn of this._waddleHooks) {
        fn(dt, { speed: this._speed, phase: this._bobPhase, goose: this.goose });
      }
    } else {
      this._idleTime += dt;
      this.goose.rotation.z *= Math.max(0, 1 - dt * 6);
      this.goose.position.y = this.position.y;
      if (parts) {
        // Slow breathing plus an occasional look-around.
        parts.body.scale.y = 0.82 + Math.sin(this._idleTime * 1.6) * 0.012;
        const glance = Math.sin(this._idleTime * 0.55) * 0.35;
        parts.head.position.x = glance * 0.06;
        parts.neck.rotation.z = glance * 0.12;
      }
      for (const fn of this._idleHooks) {
        fn(dt, { idleTime: this._idleTime, goose: this.goose });
      }
    }
  }

  /**
   * Place the camera rig for the current mode.
   * @param {number} lerp 0–1 smoothing factor (1 = snap).
   * @private
   */
  _applyCamera(lerp) {
    if (!this.rig) return;

    // A rig yaw of θ+π makes the camera look along heading (sin θ, 0, cos θ),
    // because a Three.js camera's view axis is its local −Z.
    if (this.mode === 'first') {
      const a = this._firstPersonAnchor;
      this.rig.position.copy(a.position);
      this.rig.rotation.y = (input.isVR ? a.yaw : this.yaw) + Math.PI;
      if (!input.isVR) {
        this.camera.position.set(0, AVATAR.eyeHeight, 0);
        this.camera.rotation.set(this.pitch, 0, 0, 'YXZ');
      } else {
        this.camera.position.set(0, 0, 0);
        this.camera.rotation.set(0, 0, 0);
      }
      return;
    }

    // Third person: anchor BEHIND the goose (opposite its heading) and above.
    const off = AVATAR.followOffset;
    const target = this._scratch.v.set(
      this.position.x - Math.sin(this.yaw) * off.z,
      this.position.y,
      this.position.z - Math.cos(this.yaw) * off.z,
    );

    if (input.isVR) {
      // The headset supplies eye height from local-floor; lift the rig by the
      // difference so the player's eyes land at the follow height.
      target.y += Math.max(0, off.y - AVATAR.eyeHeight);
      this.rig.position.lerp(target, lerp);
      this.rig.rotation.y = this.yaw + Math.PI;
      this.camera.position.set(0, 0, 0);
      this.camera.rotation.set(0, 0, 0);
    } else {
      this.rig.position.lerp(target, lerp);
      this.rig.rotation.y = this.yaw + Math.PI;
      this.camera.position.set(0, off.y, 0);
      // Look down at the goose's shoulders.
      const drop = off.y - AVATAR.followLookHeight;
      const basePitch = -Math.atan2(drop, Math.max(0.1, off.z));
      this.camera.rotation.set(basePitch + this.pitch, 0, 0, 'YXZ');
    }
  }

  /** Keep the goose on the midway rather than wandering into the void. */
  _clampToHub() {
    const half = HUB.groundSize / 2 - 2;
    this.position.x = THREE.MathUtils.clamp(this.position.x, -half, half);
    this.position.z = THREE.MathUtils.clamp(this.position.z, -half, half);
  }
}

/** Shortest-path angle interpolation. */
function angleLerp(a, b, t) {
  let diff = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (diff < -Math.PI) diff += Math.PI * 2;
  return a + diff * t;
}

/** The one goose controller. */
export const gooseController = new GooseController();
